// Ctrl+K: jump anywhere. Searches pages and actions instantly, and projects, plots (address, postcode, RRN,
// GUID) and companies on the server as you type.
import { useDeferredValue, useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { Area } from '@briary/shared'
import { api, qs } from '../lib/api'
import { can, useAuth } from '../lib/auth'
import { useRouter } from '../lib/router'
import { navFor } from '../modules'
import { Icon, Modal } from '../ui'
import { canOpenPortalPath } from '../lib/portal-modules'
import { canOpenStaffPath } from '../lib/staff-modules'
import { canOpenSensitiveMoneyPath } from '../lib/sensitive-money-access'
import { withPortalModuleLinks } from '../modules/portal/extra-modules'
import { withArchiveLink } from '../modules/legacy-archive/entry'
import type { IconName } from '../ui/icons'

interface Item {
  key: string
  label: string
  hint: string
  icon: IconName
  run: () => void
}

interface SearchResult {
  projects: { id: number; name: string; project_number: string | null; archived: boolean; company_name: string | null }[]
  plots: { id: number; plot_no: string; site_id: number; address1: string | null; postcode: string | null; rrn: string | null; project_name: string }[]
  companies: { id: number; name: string }[]
}

export function CommandPalette({ area, onClose }: { area: Area; onClose: () => void }) {
  const { me, signOut } = useAuth()
  const { navigate } = useRouter()
  const [q, setQ] = useState('')
  const [sel, setSel] = useState(0)
  const dq = useDeferredValue(q.trim())

  const go = useMemo(
    () => (path: string) => () => {
      onClose()
      navigate(path)
    },
    [navigate, onClose],
  )

  const pages = useMemo<Item[]>(() => {
    const nav = withArchiveLink(withPortalModuleLinks(navFor(area, (p) => can(me, p)), area), area, me?.isSuperadmin === true)
      .flatMap((g) => g.items.filter((i) => !i.soon && (area !== 'portal' || canOpenPortalPath(me, i.path)) && canOpenStaffPath(me, i.path) && canOpenSensitiveMoneyPath(me, i.path)).map((i) => ({ key: `page:${i.path}`, label: i.label, hint: g.label, icon: i.icon, run: go(i.path) })))
    return [
      ...nav,
      { key: 'page:account', label: 'Account & security', hint: 'Account', icon: 'shield', run: go('/account') },
      { key: 'action:signout', label: 'Sign out', hint: 'Account', icon: 'logout', run: () => void signOut() },
    ]
  }, [area, me, go, signOut])

  const searchable = area !== 'subcon'
  const { data, isFetching } = useQuery({
    queryKey: ['search', dq],
    queryFn: () => api.get<SearchResult>(`/api/search${qs({ q: dq })}`),
    enabled: searchable && dq.length >= 2,
    staleTime: 30_000,
  })

  const items = useMemo<Item[]>(() => {
    const t = dq.toLowerCase()
    const pageHits = t ? pages.filter((i) => i.label.toLowerCase().includes(t) || i.hint.toLowerCase().includes(t)) : pages
    if (!data || dq.length < 2) return pageHits
    const base = area === 'portal' ? '/portal/sites' : '/admin/projects'
    return [
      ...data.projects.map((p) => ({
        key: `project:${p.id}`,
        label: p.name,
        hint: [p.project_number, p.company_name, p.archived ? 'archived' : ''].filter(Boolean).join(' · ') || 'Project',
        icon: 'folder' as IconName,
        run: go(`${base}/${p.id}`),
      })),
      ...data.plots.map((p) => ({
        key: `plot:${p.id}`,
        label: `Plot ${p.plot_no}${p.address1 ? ` — ${p.address1}` : ''}`,
        hint: [p.project_name, p.postcode, p.rrn].filter(Boolean).join(' · '),
        icon: 'home' as IconName,
        run: go(`${base}/${p.site_id}?plot=${p.id}`),
      })),
      ...data.companies.map((c) => ({ key: `company:${c.id}`, label: c.name, hint: 'Company', icon: 'building' as IconName, run: go(`/admin/companies/${c.id}`) })),
      ...pageHits,
    ]
  }, [dq, pages, data, area, go])

  useEffect(() => { setSel(0) }, [items.length])

  return (
    <Modal onClose={onClose} className="palette">
      <input
        autoFocus
        placeholder={searchable ? 'Search projects, plots, addresses, RRNs, companies, pages…' : 'Search pages…'}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault()
            setSel((s) => Math.min(s + 1, items.length - 1))
          } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            setSel((s) => Math.max(s - 1, 0))
          } else if (e.key === 'Enter') {
            items[sel]?.run()
          }
        }}
        aria-label="Search"
      />
      <div className="palette-list" role="listbox">
        {items.length === 0 && !isFetching && <div className="empty" style={{ padding: 24 }}>Nothing matches “{q}”.</div>}
        {items.map((i, idx) => (
          <button key={i.key} className="palette-item" role="option" aria-selected={idx === sel} onMouseEnter={() => setSel(idx)} onClick={i.run}>
            <Icon name={i.icon} width={16} height={16} />
            <span className="ell">{i.label}</span>
            <small className="ell" style={{ maxWidth: '45%' }}>{i.hint}</small>
          </button>
        ))}
      </div>
      <div className="palette-foot">
        {isFetching ? 'Searching…' : <><kbd>↑</kbd> <kbd>↓</kbd> to move · <kbd>Enter</kbd> to open · <kbd>Esc</kbd> to close</>}
      </div>
    </Modal>
  )
}
